import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Container from '../ui/Container';
import useFetch from '../../hooks/useFetch';
import { getProjects, imageUrl, parseImages } from '../../lib/api';

export default function FeaturedProject() {
  const { data, isLoading, error } = useFetch(getProjects, []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Reiniciar el índice de imagen cuando cambie el proyecto destacado
  useEffect(() => {
    setActiveImageIndex(0);
  }, [currentIndex]);

  if (error) return null;

  // Filtrar proyectos destacados explícitamente marcados o tomar los primeros como fallback
  const featuredProjects =
    data && data.length > 0
      ? data.filter(p => p.destacado === true || p.destacado === 'true')
      : [];

  const projectsToDisplay =
    featuredProjects.length > 0
      ? featuredProjects
      : data && data.length > 0
      ? data.slice(0, 3)
      : [];

  const total = projectsToDisplay.length;

  // 0 destacados y no cargando: no renderizar caja vacía
  if (!isLoading && total === 0) {
    return null;
  }

  const activeProject = projectsToDisplay[currentIndex] || null;
  const projectImages = activeProject ? parseImages(activeProject.imagen) : [];
  const safeImageIndex = activeImageIndex < projectImages.length ? activeImageIndex : 0;
  const currentImageName = projectImages[safeImageIndex] || '';
  const mainSrc = currentImageName ? imageUrl(currentImageName) : '';

  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? total - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev === total - 1 ? 0 : prev + 1));
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex(prev => (prev === 0 ? projectImages.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex(prev => (prev === projectImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      as="section"
      py={{ base: 12, md: 16 }}
      bg="surface.muted"
      aria-label="Proyectos destacados"
    >
      <Container>
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacing={{ base: 8, lg: 12 }}
          alignItems="center"
        >
          {/* Galería de imágenes del proyecto */}
          {isLoading || !activeProject ? (
            <Skeleton borderRadius="lg" h={{ base: '260px', lg: '380px' }} />
          ) : (
            <Stack spacing={3}>
              <Box position="relative" borderRadius="lg" overflow="hidden">
                <Image
                  src={mainSrc}
                  alt={`${activeProject.nombre} - Imagen ${safeImageIndex + 1}`}
                  borderRadius="lg"
                  w="100%"
                  h={{ base: '260px', lg: '380px' }}
                  objectFit="cover"
                  loading="lazy"
                  decoding="async"
                  transition="all 0.3s ease"
                />

                {/* Controles de imágenes si el proyecto tiene más de 1 foto */}
                {projectImages.length > 1 && (
                  <>
                    <IconButton
                      aria-label="Imagen anterior del proyecto"
                      icon={<FiChevronLeft />}
                      size="sm"
                      position="absolute"
                      left={2}
                      top="50%"
                      transform="translateY(-50%)"
                      bg="blackAlpha.600"
                      color="white"
                      _hover={{ bg: 'blackAlpha.800' }}
                      borderRadius="full"
                      onClick={handlePrevImage}
                    />
                    <IconButton
                      aria-label="Imagen siguiente del proyecto"
                      icon={<FiChevronRight />}
                      size="sm"
                      position="absolute"
                      right={2}
                      top="50%"
                      transform="translateY(-50%)"
                      bg="blackAlpha.600"
                      color="white"
                      _hover={{ bg: 'blackAlpha.800' }}
                      borderRadius="full"
                      onClick={handleNextImage}
                    />
                    <Box
                      position="absolute"
                      bottom={3}
                      right={3}
                      bg="blackAlpha.700"
                      color="white"
                      px={2.5}
                      py={0.5}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      {safeImageIndex + 1} / {projectImages.length}
                    </Box>
                  </>
                )}
              </Box>

              {/* Tira de miniaturas interactivas si el proyecto tiene múltiples fotos */}
              {projectImages.length > 1 && (
                <HStack spacing={2.5} overflowX="auto" py={1}>
                  {projectImages.map((imgName, idx) => (
                    <Box
                      key={imgName + idx}
                      as="button"
                      onClick={() => setActiveImageIndex(idx)}
                      borderRadius="md"
                      overflow="hidden"
                      border="2px solid"
                      borderColor={idx === safeImageIndex ? 'brand.700' : 'transparent'}
                      opacity={idx === safeImageIndex ? 1 : 0.6}
                      _hover={{ opacity: 1 }}
                      transition="all 0.2s"
                      flexShrink={0}
                    >
                      <Image
                        src={imageUrl(imgName)}
                        alt={`Miniatura ${idx + 1}`}
                        w="56px"
                        h="56px"
                        objectFit="cover"
                      />
                    </Box>
                  ))}
                </HStack>
              )}
            </Stack>
          )}

          {/* Información del proyecto */}
          <Stack spacing={4}>
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
              <Text
                fontSize="xs"
                fontWeight={700}
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="accent.default"
              >
                Proyecto destacado
              </Text>

              {/* Controles de navegación entre proyectos solo si hay más de 1 proyecto */}
              {!isLoading && total > 1 && (
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Proyecto anterior"
                    icon={<FiChevronLeft />}
                    size="sm"
                    variant="outline"
                    borderRadius="full"
                    onClick={handlePrev}
                  />
                  <Text fontSize="12px" color="text.secondary" px={1}>
                    Proyecto {currentIndex + 1} / {total}
                  </Text>
                  <IconButton
                    aria-label="Proyecto siguiente"
                    icon={<FiChevronRight />}
                    size="sm"
                    variant="outline"
                    borderRadius="full"
                    onClick={handleNext}
                  />
                </HStack>
              )}
            </Flex>

            {isLoading || !activeProject ? (
              <Stack spacing={3}>
                <Skeleton height="36px" width="80%" />
                <Skeleton height="20px" width="100%" />
                <Skeleton height="20px" width="60%" />
              </Stack>
            ) : (
              <>
                <Heading as="h2" size="h2" fontSize={{ base: '22px', md: '28px' }}>
                  {activeProject.nombre}
                </Heading>
                <Text color="text.secondary" fontSize="15px" lineHeight={1.6}>
                  {activeProject.descripcion}
                </Text>
                <Stack direction="row" spacing={8} fontSize="sm" pt={2}>
                  {activeProject.empresa && (
                    <Box>
                      <Text color="text.secondary" fontSize="xs" fontWeight={500}>
                        Cliente
                      </Text>
                      <Text fontWeight={600} color="text.primary">
                        {activeProject.empresa}
                      </Text>
                    </Box>
                  )}
                  {activeProject.estado && (
                    <Box>
                      <Text color="text.secondary" fontSize="xs" fontWeight={500}>
                        Estado
                      </Text>
                      <Text fontWeight={600} color="text.primary">
                        {activeProject.estado}
                      </Text>
                    </Box>
                  )}
                  {activeProject.ubicacion && (
                    <Box>
                      <Text color="text.secondary" fontSize="xs" fontWeight={500}>
                        Ubicación
                      </Text>
                      <Text fontWeight={600} color="text.primary">
                        {activeProject.ubicacion}
                      </Text>
                    </Box>
                  )}
                </Stack>
              </>
            )}

            <Button
              as={RouterLink}
              to="/projects"
              variant="primary"
              alignSelf="flex-start"
              mt={2}
            >
              Ver casos de éxito
            </Button>
          </Stack>
        </SimpleGrid>
      </Container>
    </Box>
  );
}
